//-- Copyright (c) 2021 TMT Insights Group, LLC
import React, { ReactNode } from 'react';
import { Fade } from '@mui/material';
import { SnackbarProvider, MaterialDesignContent, SnackbarKey } from 'notistack';
import { useToast } from './_common/Toast';
import styled from '@emotion/styled';
import ReleaseNotesSnackbar from './_common/ReleaseNotes/ReleaseNotesSnackbar';
import LoadingSnackbar from './_common/LoadingSnackbar';

// fakes fn to avoid race conditions and exceptions if any of them are called before initialization
const fakeFn = () => {};

export let globallyAvailableSnackBar: {
  error: (message: string) => void;
  warn: (message: string) => void;
  info: (message: string) => void;
  success: (message: string) => void;
  closeSnackbar: (key: SnackbarKey) => void;
} = {
  error: fakeFn,
  warn: fakeFn,
  info: fakeFn,
  success: fakeFn,
  closeSnackbar: fakeFn
};

declare module 'notistack' {
  interface VariantOverrides {
    releaseNotes: true;
    loading: true;
  }
}

function Snackbar({ children }: { children: ReactNode }) {
  const GloballyStoredSnackbarProvider = React.useMemo(
    () => () => {
      globallyAvailableSnackBar = useToast();
      return null;
    },
    []
  );

  return (
    <SnackbarProvider
      maxSnack={4}
      TransitionComponent={Fade}
      autoHideDuration={3000}
      Components={{
        success: StyledMaterialDesignContent,
        error: StyledMaterialDesignContent,
        info: StyledMaterialDesignContent,
        warning: StyledMaterialDesignContent,
        loading: LoadingSnackbar,
        releaseNotes: ReleaseNotesSnackbar
      }}
    >
      <GloballyStoredSnackbarProvider />
      {children}
    </SnackbarProvider>
  );
}

export default Snackbar;

const StyledMaterialDesignContent = styled(MaterialDesignContent)`
  &.notistack-MuiContent {
    pointer-events: auto;
    min-width: 100px;
    max-width: 500px;
    width: 100%;
    border-radius: 23px;
    font-size: 1rem;
    font-weight: ${({ theme }) => theme.typography.fontWeightMedium};

    & > div {
      display: flex;
      flex-direction: row;
      align-items: center;
      column-gap: 10px;

      svg {
        width: 25px;
        height: 25px;
        min-width: 25px;
        min-height: 25px;
      }

      span {
        max-height: 100px;
        overflow-y: auto;
      }
    }

    .MuiButton-root {
      height: 30px;
      padding: 0 10px;
      margin-left: 10px;
      color: #fff;
      border-radius: 50px;
      font-size: 0.85rem;
      overflow: hidden;
      background-color: rgba(255, 255, 255, 0.3);
      transition: background-color 0.3s ease-in-out;

      &:hover {
        background-color: rgba(255, 255, 255, 0.4);
        transition: background-color 0.3s ease-in-out;
      }
    }
  }
`;
